// @flow
import React, { Component } from 'react';
import type { Errors } from 'types/errors';

import _ from 'lodash';
import classnames from 'classnames';

import {
  PASSENGER_BEHAVIOURS,
  PASSENGER_BEHAVIOUR_TYPES,
  PASSENGER_BEHAVIOUR_PARAMS,
  hasPassengerBehaviour,
} from 'utils/library-utils';

type Props = {
  defaultValue?: Object,
  disableLoginFields?: boolean,
};

export default class PassengerForm extends Component {
  static defaultProps = {
    defaultValue: {
      behaviour: {
        type: PASSENGER_BEHAVIOURS.MANUAL,
        params: {},
      },
    },
    disableLoginFields: false,
  }

  constructor(props: Props) {
    super(props);
    const value: Object = _.cloneDeep(this.props.defaultValue || {});
    if (value.behaviour.params) {
      PASSENGER_BEHAVIOUR_PARAMS[value.behaviour.type].forEach((fieldObject) => {
        value.behaviour.params[fieldObject.name] = fieldObject.renderValue(
          value.behaviour.params[fieldObject.name]);
      });
    }
    this.state = {
      value,
      passengerIDValid: true,
      behaviourValid: true,
    };
  }

  state: {
    value: Object,
    passengerIDValid: boolean,
    behaviourValid: boolean,
  };

  getValue() {
    const value = _.cloneDeep(this.state.value);
    const behaviourType = _.get(this.state.value, 'behaviour.type');
    if (hasPassengerBehaviour(behaviourType) && value.behaviour.params) {
      PASSENGER_BEHAVIOUR_PARAMS[behaviourType].forEach((fieldObject) => {
        const fieldName = fieldObject.name;
        if (value.behaviour.params[fieldName] !== '' &&
          value.behaviour.params[fieldName] != null) {
          value.behaviour.params[fieldName] =
            fieldObject.processValue(value.behaviour.params[fieldName]);
        } else {
          delete value.behaviour.params[fieldName];
        }
      });
    }
    return value;
  }

  props: Props;

  isValid() {
    const passengerIDValid = this.validatePassengerID();
    const behaviourValid = this.validateBehaviour();
    this.setState({
      passengerIDValid,
      behaviourValid,
    });
    return passengerIDValid && behaviourValid;
  }

  validatePassengerID() {
    return this.props.disableLoginFields ||
      (this.state.value.cohortPassengerID != null && this.state.value.cohortPassengerID !== '');
  }

  validateBehaviour() {
    const value = _.cloneDeep(this.state.value);
    const behaviourType = _.get(value, 'behaviour.type');
    const params = _.get(value, 'behaviour.params');

    function validateParam(field) {
      return _.get(params, field.name, false) && field.validate(params[field.name]);
    }

    return PASSENGER_BEHAVIOUR_PARAMS[behaviourType].every(validateParam);
  }

  errors(): Errors {
    const allErrors: Errors = [];
    if (!this.validatePassengerID()) {
      allErrors.push({ message: 'Invalid Passenger ID' });
    }

    if (!this.validateBehaviour()) {
      allErrors.push({ message: 'Invalid Behaviour Params' });
    }

    return allErrors;
  }

  render() {
    const behaviourType = _.get(this.state.value, 'behaviour.type');
    return (
      <div role="form">
        <div className="notification is-warning" role="alert">
          These passengers have to exist in the passenger databases of the respective environments.
        </div>
        <div className="control is-grouped">
          <p className="control is-expanded">
            <label className="label" htmlFor="name">Name</label>
            <input
              className="input"
              type="text"
              id="name"
              placeholder="For your own reference. E.g. Hannah Test Account"
              value={this.state.value.name || ''}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    name: event.target.value,
                  },
                }));
              }}
            />
          </p>
        </div>
        <div className="control is-grouped">
          <p className="control is-expanded">
            <label className="label" htmlFor="passenger-id">Passenger ID</label>
            <input
              className={classnames('input', { 'is-danger': !this.state.passengerIDValid })}
              type="text"
              id="passenger-id"
              value={this.state.value.cohortPassengerID || ''}
              disabled={this.props.disableLoginFields}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    cohortPassengerID: event.target.value,
                  },
                }), () => {
                  this.setState({
                    passengerIDValid: this.validatePassengerID(),
                  });
                });
              }}
            />
          </p>
          <p className="control is-expanded">
            <label className="label" htmlFor="api-token">API Token</label>
            <input
              className={classnames('input')}
              type={this.props.disableLoginFields ? 'password' : 'text'}
              id="api-token"
              value={this.props.disableLoginFields ?
                'REDACTEDREDACTEDREDACTEDAPITOKEN' : (this.state.value.apiToken || '')
              }
              disabled={this.props.disableLoginFields}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    apiToken: event.target.value,
                  },
                }));
              }}
            />
          </p>
        </div>
        <label className="label" htmlFor="behaviour">Behaviour</label>
        <p className="control">
          <span className="select">
            <select
              id="behaviour"
              value={behaviourType}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    behaviour: {
                      type: event.target.value,
                    },
                  },
                }), () => {
                  this.setState({
                    behaviourValid: this.validateBehaviour(),
                  });
                });
              }}
            >
              {PASSENGER_BEHAVIOUR_TYPES.map(behaviour =>
                <option key={behaviour.value} value={behaviour.value}>
                  {behaviour.name}
                </option>
              )}
            </select>
          </span>
        </p>
        {hasPassengerBehaviour(behaviourType) &&
          <div>
            <label className="label" htmlFor="behaviour-parameters">Behaviour Parameters</label>
            {PASSENGER_BEHAVIOUR_PARAMS[behaviourType].map(field =>
              <div className="columns" key={field.name}>
                <div className="column is-one-third">
                  <label className="label" htmlFor={field.name}>
                    <code>{field.name}</code>
                  </label>
                </div>
                <div className="column is-two-thirds">
                  <input
                    className="input"
                    id={field.name}
                    type={PASSENGER_BEHAVIOUR_PARAMS[behaviourType][field.name]}
                    placeholder={field.hint || _.startCase(field.name)}
                    value={_.get(this.state.value, `behaviour.params.${field.name}`, '')}
                    onChange={(event) => {
                      this.setState(_.merge(this.state, {
                        value: {
                          behaviour: {
                            params: {
                              [field.name]: event.target.value,
                            },
                          },
                        },
                      }));
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        }
      </div>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/js/components/entities/PassengerForm.jsx
 **/